// Direction-2 sidebar: sections Vue / Organisation / Système, 212/56px width.

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  IconDashboard,
  IconOperations,
  IconBank,
  IconBudget,
  IconTag,
  IconCategory,
  IconRobot,
  IconKey,
  IconImport,
  IconWorkflow,
  IconSettings,
  IconAdmin,
  IconAudit,
  IconSun,
  IconMoon,
  IconMenu,
  IconLogout,
  IconProps,
} from '../../design/icons';

export type PageId =
  | 'dashboard'
  | 'operations'
  | 'accounts'
  | 'budgets'
  | 'tags'
  | 'categories'
  | 'auto-categorization'
  | 'keywords'
  | 'import'
  | 'workflows'
  | 'workflow-run'
  | 'config'
  | 'admin'
  | 'audit-logs';

interface NavEntry {
  id: PageId;
  label: string;
  Icon: React.FC<IconProps>;
  kbd?: string;
  adminOnly?: boolean;
}

const VIEW_ITEMS: NavEntry[] = [
  { id: 'dashboard', label: 'Dashboard', Icon: IconDashboard, kbd: 'D' },
  { id: 'operations', label: 'Opérations', Icon: IconOperations, kbd: 'O' },
  { id: 'accounts', label: 'Comptes bancaires', Icon: IconBank, kbd: 'C' },
  { id: 'budgets', label: 'Budgets', Icon: IconBudget, kbd: 'B' },
  { id: 'tags', label: 'Tags', Icon: IconTag },
];

const ORG_ITEMS: NavEntry[] = [
  { id: 'categories', label: 'Catégories', Icon: IconCategory },
  { id: 'auto-categorization', label: 'Catégorisation', Icon: IconRobot },
  { id: 'keywords', label: 'Mots-clés', Icon: IconKey },
  { id: 'import', label: 'Import CSV', Icon: IconImport },
  { id: 'workflows', label: 'Workflows', Icon: IconWorkflow },
];

const SYS_ITEMS: NavEntry[] = [
  { id: 'config', label: 'Configuration', Icon: IconSettings },
  { id: 'admin', label: 'Administration', Icon: IconAdmin, adminOnly: true },
  { id: 'audit-logs', label: "Logs d'audit", Icon: IconAudit },
];

export interface SidebarProps {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentPage,
  onNavigate,
  collapsed,
  onToggleCollapse,
}) => {
  const { user, logout } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';

  const canAccessAdmin =
    hasAnyPermission(['admin.users.read', 'admin.groups.read', 'admin.database.manage']) ||
    user?.identifiant === 'admin';

  const initials = ((user?.prenom?.[0] || '') + (user?.nom?.[0] || '')).toUpperCase() || 'U';

  const renderItem = (item: NavEntry, showKbd = false) => {
    if (item.adminOnly && !canAccessAdmin) return null;
    const IC = item.Icon;
    const active = currentPage === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onNavigate(item.id)}
        title={collapsed ? item.label : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: collapsed ? 7 : '5px 8px',
          borderRadius: 4,
          background: active ? 'var(--panel-3)' : 'transparent',
          border: 'none',
          color: active ? 'var(--text)' : 'var(--text-muted)',
          fontSize: 12.5,
          fontWeight: active ? 600 : 500,
          width: '100%',
          cursor: 'pointer',
          textAlign: 'left',
          justifyContent: collapsed ? 'center' : 'flex-start',
          whiteSpace: 'nowrap',
          transition: 'all .1s',
          fontFamily: 'inherit',
          minHeight: collapsed ? 30 : 'auto',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'var(--panel-3)';
            e.currentTarget.style.color = 'var(--text)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }
        }}
      >
        <IC size={15} stroke={1.7} />
        {!collapsed && <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
        {!collapsed && showKbd && item.kbd && (
          <kbd
            style={{
              marginLeft: 'auto',
              fontFamily: 'inherit',
              fontSize: 10,
              color: 'var(--text-faint)',
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              padding: '1px 5px',
              borderRadius: 3,
            }}
          >
            {item.kbd}
          </kbd>
        )}
      </button>
    );
  };

  const sectionLabel = (label: string) =>
    !collapsed && (
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 600,
          padding: '10px 8px 4px',
        }}
      >
        {label}
      </div>
    );

  return (
    <aside
      style={{
        width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)',
        background: 'var(--panel-2)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 8px',
        transition: 'width 0.15s ease',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Brand */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 6px 12px',
          borderBottom: '1px solid var(--border)',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            background: 'var(--accent)',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 11,
            flex: '0 0 22px',
          }}
        >
          T
        </div>
        {!collapsed && (
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <h1
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                margin: 0,
                letterSpacing: '-0.01em',
                color: 'var(--text)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              TrackMyBalance
            </h1>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--text-faint)',
                marginTop: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  display: 'inline-block',
                }}
              />
              {user?.identifiant || 'workspace'}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? 'Étendre' : 'Réduire'}
          className="tmb-iconbtn"
          style={{ height: 22, width: 22 }}
        >
          <IconMenu size={13} />
        </button>
      </div>

      {/* Navigation */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, overflowY: 'auto' }}>
        {sectionLabel('Vue')}
        {VIEW_ITEMS.map((it) => renderItem(it, true))}
        {sectionLabel('Organisation')}
        {ORG_ITEMS.map((it) => renderItem(it))}
        {sectionLabel('Système')}
        {SYS_ITEMS.map((it) => renderItem(it))}
      </nav>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          marginTop: 8,
        }}
      >
        <button
          type="button"
          onClick={toggleTheme}
          title={dark ? 'Mode clair' : 'Mode sombre'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: collapsed ? 7 : '5px 8px',
            borderRadius: 4,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 12.5,
            fontWeight: 500,
            width: '100%',
            cursor: 'pointer',
            textAlign: 'left',
            justifyContent: collapsed ? 'center' : 'flex-start',
            fontFamily: 'inherit',
          }}
        >
          {dark ? <IconSun size={15} /> : <IconMoon size={15} />}
          {!collapsed && <span>{dark ? 'Mode clair' : 'Mode sombre'}</span>}
        </button>
        <button
          type="button"
          onClick={() => logout()}
          title="Déconnexion"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: collapsed ? 7 : '5px 8px',
            borderRadius: 4,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 12.5,
            fontWeight: 500,
            width: '100%',
            cursor: 'pointer',
            textAlign: 'left',
            justifyContent: collapsed ? 'center' : 'flex-start',
            fontFamily: 'inherit',
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              background: dark ? '#283439' : '#dbe4e0',
              color: dark ? '#b9d3c8' : '#335048',
              display: 'grid',
              placeItems: 'center',
              fontSize: 10,
              fontWeight: 600,
              flex: '0 0 20px',
            }}
          >
            {initials}
          </div>
          {!collapsed && (
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.prenom || user?.identifiant || 'Compte'}
            </span>
          )}
          {!collapsed && <IconLogout size={13} />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
